/**
 * PartnerReviewDrawer — 반장님 시공이력/거래처 거래이력 + 작업건별 평가메모 슬라이드오버.
 * 기획서 F. 나의 시공파트너 "시공이력&평가메모" / G. 나의 자재파트너 "거래이력&평가메모" 화면 기반.
 * (Phase 2 — UI 개편: 자주 쓰는 문구 칩·크게보기·3상태 배지·이력 타임라인)
 *
 * 평가(별점·메모) 단위는 "현장(SITE) 하나당 종합 1건"이 아니라 "작업건(현장+공종) 하나당 1건"이다.
 * 같은 현장이라도 공종이 여러 개면 평가 행도 여러 개 나온다 — 이력 item(작업건) 하나가 평가 1건이다.
 * (BE가 item마다 workType·rating·evalMemo를 내려주고, 저장 요청에 workTypeCode를 키로 받는다.)
 *
 * 레이아웃은 좌우 2열 구조를 따른다 — 왼쪽(가변폭)에 현장명·공종명·고객명·상태배지·이력 타임라인을,
 * 오른쪽(고정폭 카드)에 별점·문구칩·메모 편집을 나란히 배치한다. 각 작업건마다 좌우 한 행이 생긴다.
 *
 * 평가 잠금: 평가는 공종 완료·입고 여부와 무관하게 언제든 가능하고, 현장이 '완료'(BE reviewLocked=true)면
 * 별점·메모 편집이 모두 잠기고 읽기만 된다. reviewLocked를 못 받으면(undefined) 잠그지 않는다.
 *
 * 권한: 사장(MASTER_YN=Y)은 누구 평가든, 직원은 본인이 작성한 평가만 수정 가능 — BE가 403으로
 * 강제하며, 저장 실패 시 BE 오류 문구를 Alert로 그대로 보여준다.
 *
 * 데이터 주의: 시공 일정확인 줄은 Planner confirm-history로 바인딩, 이슈보고·작업요청 소요시간은 WP6 소관.
 * 자재 타임라인 4줄(발주정보·양중요청·배송정보·입고준수)은 Planner 거래이력 요약(order-history)을 거래 건(orderId)으로 병합하고, 못 받으면 해당 줄은 "—".
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Drawer, Rating, Skeleton } from '@indipia/ui-core';
import type { BadgeStatus } from '@indipia/ui-core';
import {
  fetchPartnerConfirmHistory,
  fetchPartnerConstructionHistory,
  upsertSiteReview as upsertPartnerSiteReview,
  type ConstructionHistoryResponse,
  type PartnerConfirmHistoryItem,
  type SiteConstructionHistory,
} from '../../shared/api/partners';
import {
  fetchVendorOrderHistory,
  fetchVendorTransactionHistory,
  upsertSiteReview as upsertVendorSiteReview,
  type TransactionHistoryResponse,
  type SiteTransactionHistory,
  type VendorOrderHistoryItem,
} from '../../shared/api/vendors';
import { isReviewLocked, vendorPhase, vendorTimelineRows } from '../../shared/vendor/vendorHistory.logic';
import { fetchWorkTypes } from '../../shared/api/work-types';

export interface PartnerReviewDrawerProps {
  kind: 'partner' | 'vendor';
  id: number;
  name: string;
  onClose: () => void;
}

type HistoryResponse = ConstructionHistoryResponse | TransactionHistoryResponse;

/** 작업건 진행 3상태 — 기간(시작일)·상태로 FE에서 계산. 배지 표시용이며 평가 가능 여부와 무관 */
type Phase = 'UPCOMING' | 'ONGOING' | 'DONE';
const PHASE_META: Record<Phase, { label: string; badge: BadgeStatus }> = {
  UPCOMING: { label: '예정', badge: 'mute' },
  ONGOING: { label: '진행중', badge: 'warning' },
  DONE: { label: '완료', badge: 'success' },
};

/** 자주 쓰는 문구 칩 — 긍정=brand(group), 부정=danger (ui-core 토큰) */
type Phrase = { label: string; tone: 'pos' | 'neg' };
const PARTNER_PHRASES: Phrase[] = [
  { label: '실력좋음', tone: 'pos' },
  { label: '꼼꼼함', tone: 'pos' },
  { label: '소통좋음', tone: 'pos' },
  { label: '일정지연', tone: 'neg' },
  { label: '품질불량', tone: 'neg' },
  { label: '뒷정리불량', tone: 'neg' },
];
const VENDOR_PHRASES: Phrase[] = [
  { label: '납기준수', tone: 'pos' },
  { label: '수량정확', tone: 'pos' },
  { label: '포장꼼꼼', tone: 'pos' },
  { label: '가격좋음', tone: 'pos' },
  { label: '납기지연', tone: 'neg' },
  { label: '수량착오', tone: 'neg' },
  { label: '파손불량', tone: 'neg' },
  { label: '오배송', tone: 'neg' },
];

const PLACEHOLDER = '—';

/** 자정 기준 오늘(로컬). 날짜 문자열(YYYY-MM-DD)을 일 단위로 비교 */
function startOfToday(): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}
function parseDay(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

/** 타임라인 한 줄: 라벨 + 값. muted=데이터 없는 placeholder 칸 */
type TimelineRow = { label: string; value: string; muted?: boolean; tone?: 'ok' };

/** 서버 시각 문자열 → 'M/D' (로컬 시각 정규식 파싱, 실패 시 null) */
function formatMonthDay(s: string | null): string | null {
  const m = s ? /(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
  return m ? `${Number(m[2])}/${Number(m[3])}` : null;
}

/** 경과 분 → 60분 미만 'N분', 24시간 미만 'N시간 M분'(분 0이면 'N시간'), 이상 'N일 N시간' */
function formatElapsed(minutes: number): string {
  if (minutes < 60) return `${minutes}분`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const m = minutes % 60;
    return m === 0 ? `${hours}시간` : `${hours}시간 ${m}분`;
  }
  return `${Math.floor(hours / 24)}일 ${hours % 24}시간`;
}

/** 일정확인 타임라인 줄 — 이력이 없으면 muted '—' */
function confirmTimelineRow(item: PartnerConfirmHistoryItem | undefined): TimelineRow {
  const label = '일정확인';
  if (!item) return { label, value: PLACEHOLDER, muted: true };
  const req = `요청 ${formatMonthDay(item.sentDtm) ?? PLACEHOLDER}`;
  const elapsed = item.elapsedMinutes != null ? ` · ${formatElapsed(item.elapsedMinutes)}` : '';
  switch (item.state) {
    case 'REPLIED_CONFIRMED':
      return { label, value: `${req} → 회신 가능${elapsed}`, tone: item.withinTwoHours === true ? 'ok' : undefined };
    case 'REPLIED_DECLINED':
      return { label, value: `${req} → 회신 불가${elapsed}`, tone: item.withinTwoHours === true ? 'ok' : undefined };
    case 'WAITING':
      return { label, value: `${req} → 회신 대기` };
    case 'EXPIRED':
      return { label, value: `${req} → 기한 지남` };
    case 'CLOSED':
      return { label, value: `${req} → 마감` };
    default:
      return { label, value: PLACEHOLDER, muted: true };
  }
}

/** 평가 1건 = 작업건(현장+공종) 하나. 현장 그룹을 공종별 item으로 평탄화한 렌더 단위 */
interface ReviewRow {
  rowKey: string;
  siteId: number;
  siteName: string | null;
  customerName: string | null;
  /** 공종코드 (= 저장 요청의 workTypeCode). null이면 평가 저장 불가 */
  workType: string | null;
  rating: number | null;
  evalMemo: string | null;
  phase: Phase;
  /** 현장 완료로 평가 잠김 (BE reviewLocked === true) */
  locked: boolean;
  /** 이력 타임라인 (시공 3줄 / 자재 4줄) */
  timeline: TimelineRow[];
}

/** 문구 토글 — 메모 끝에 ', '로 이어 넣고, 이미 있으면 빼기(기획 fhPhrase/mhPhrase와 동일) */
function togglePhrase(value: string, phrase: string): string {
  if (value.includes(phrase)) {
    return value
      .split(phrase)
      .join('')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');
  }
  const t = value.trim();
  return t ? `${t}${t.endsWith(',') ? ' ' : ', '}${phrase}` : phrase;
}

export function PartnerReviewDrawer({ kind, id, name, onClose }: PartnerReviewDrawerProps) {
  const isPartner = kind === 'partner';
  const queryClient = useQueryClient();
  const queryKey = [isPartner ? 'partner-construction-history' : 'vendor-transaction-history', id];

  const { data, isLoading } = useQuery<HistoryResponse>({
    queryKey,
    queryFn: () => (isPartner ? fetchPartnerConstructionHistory(id) : fetchVendorTransactionHistory(id)),
  });

  // 일정확인 이력(Planner) — 실패해도 에러 화면 없이 해당 줄만 '—'
  const { data: confirmHistory } = useQuery({
    queryKey: ['partner-confirm-history', id],
    queryFn: () => fetchPartnerConfirmHistory(id),
    enabled: isPartner,
  });
  const confirmByTask = useMemo(() => {
    const map = new Map<number, PartnerConfirmHistoryItem>();
    (confirmHistory ?? []).forEach((c) => map.set(c.taskId, c));
    return map;
  }, [confirmHistory]);

  // 자재 거래이력 요약(Planner) — 거래 건(orderId = 원장 행 ID)별 발주·양중·배송·입고. 실패해도 에러 화면 없이 해당 줄만 '—'
  const { data: orderHistory } = useQuery({
    queryKey: ['vendor-order-history', id],
    queryFn: () => fetchVendorOrderHistory(id),
    enabled: !isPartner,
  });
  const orderByRow = useMemo(() => {
    const map = new Map<number, VendorOrderHistoryItem>();
    (orderHistory ?? []).forEach((h) => map.set(h.orderVendorId, h));
    return map;
  }, [orderHistory]);

  // 공종코드 → 공종명 변환용 (Phase 1 보존)
  const { data: workTypes } = useQuery({ queryKey: ['work-types'], queryFn: fetchWorkTypes });
  const workTypeName = useMemo(() => {
    const map = new Map<string, string>();
    (workTypes ?? []).forEach((w) => map.set(w.workTypeCode, w.workTypeName));
    return (code: string | null) => (code ? map.get(code) ?? code : null);
  }, [workTypes]);

  // 메모 수정 중인 작업건(rowKey) + 그 입력중인 텍스트
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [errorState, setErrorState] = useState<{ key: string; message: string } | null>(null);

  const { mutate: save } = useMutation({
    mutationFn: (vars: {
      rowKey: string;
      siteId: number;
      workTypeCode: string;
      rating?: number | null;
      evalMemo?: string | null;
    }) =>
      isPartner
        ? upsertPartnerSiteReview(id, vars.siteId, {
            workTypeCode: vars.workTypeCode,
            rating: vars.rating,
            evalMemo: vars.evalMemo,
          })
        : upsertVendorSiteReview(id, vars.siteId, {
            workTypeCode: vars.workTypeCode,
            rating: vars.rating,
            evalMemo: vars.evalMemo,
          }),
    onSuccess: (_data, vars) => {
      setErrorState(null);
      if (vars.rowKey === editingKey) setEditingKey(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: unknown, vars) => {
      const res = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      const message =
        res?.data?.message ||
        (res?.status === 403 ? '본인이 작성한 평가만 수정할 수 있습니다.' : '평가를 저장하지 못했어요. 잠시 후 다시 시도해 주세요.');
      setErrorState({ key: vars.rowKey, message });
    },
  });

  const phrases = isPartner ? PARTNER_PHRASES : VENDOR_PHRASES;

  // 현장 그룹 → 작업건(현장+공종) 단위로 평탄화 + 3상태 계산 + 타임라인 구성
  const rows: ReviewRow[] = isPartner
    ? ((data?.sites as SiteConstructionHistory[] | undefined) ?? []).flatMap((site) =>
        site.items.map((item) => {
          // 완료=status COMPLETE / 예정=시작일>오늘 / 그 외 진행중
          const start = parseDay(item.periodFrom);
          const phase: Phase =
            item.status === 'COMPLETE' ? 'DONE' : start && start > startOfToday() ? 'UPCOMING' : 'ONGOING';
          const reportVal =
            phase === 'DONE' ? item.periodTo ?? PLACEHOLDER : phase === 'ONGOING' ? '진행 중' : '예정';
          return {
            rowKey: `c-${item.constructionId}`,
            siteId: site.siteId,
            siteName: site.siteName,
            customerName: site.customerName,
            workType: item.workType,
            rating: item.rating,
            evalMemo: item.evalMemo,
            phase,
            locked: isReviewLocked(item),
            timeline: [
              // 일정확인 알림톡 회신: Planner confirm-history를 constructionId(=taskId)로 병합
              confirmTimelineRow(confirmByTask.get(item.constructionId)),
              // 작업요청→보고: 시작일(요청)·종료일(보고)은 응답에 있음 → 바인딩
              {
                label: '작업요청',
                value: `요청 ${item.periodFrom ?? PLACEHOLDER} → 보고 ${reportVal}`,
                muted: !item.periodFrom,
              },
              // 이슈보고: 응답에 없음 → Phase 4 데이터 연구 후 바인딩
              { label: '이슈보고', value: PLACEHOLDER, muted: true },
            ],
          };
        }),
      )
    : ((data?.sites as SiteTransactionHistory[] | undefined) ?? []).flatMap((site) =>
        site.items.map((item) => {
          // 집계(SV1)를 못 받았을 때의 추정용 — 입고예정일 경과 시 완료 추정(실입고 데이터 없음), 예정일이 없으면 발주 존재로 완료
          const exp = parseDay(item.expectedDate);
          const ord = parseDay(item.orderedDate);
          const today = startOfToday();
          const complete = exp ? exp <= today : !!item.orderedDate;
          const summary = orderByRow.get(item.orderId);
          // 진행 3상태: 집계가 있으면 원장 상태(RECEIVED=완료)·발주일로 판정. 집계를 못 받았으면 기존 추정 규칙 유지
          // (입고예정일 경과 시 완료 추정, 예정일이 없으면 발주 존재로 완료, 예정=발주일>오늘, 그 외 진행중).
          const phase: Phase = summary
            ? vendorPhase(summary, item.orderedDate)
            : complete ? 'DONE' : ord && ord > today ? 'UPCOMING' : 'ONGOING';
          return {
            rowKey: `v-${item.orderId}`,
            siteId: site.siteId,
            siteName: site.siteName,
            customerName: site.customerName,
            workType: item.workType,
            rating: item.rating,
            evalMemo: item.evalMemo,
            phase,
            locked: isReviewLocked(item),
            // 집계가 있으면 4줄 문구 그대로(입고준수는 약속일 없으면 줄 생략), 없으면 4줄 모두 '—'
            timeline: summary
              ? vendorTimelineRows(summary)
              : ['발주정보', '양중요청', '배송정보', '입고준수'].map((label) => ({ label, value: PLACEHOLDER, muted: true })),
          };
        }),
      );

  const headerHint = isPartner
    ? '공사스케줄에 공종별로 배정된 시공 건을 모아 보여줘요. 별점과 메모는 언제든 남길 수 있고, 현장이 완료되면 잠겨요.'
    : '현장별 발주 건을 모아 보여줘요. 별점과 메모는 언제든 남길 수 있고, 현장이 완료되면 잠겨요.';

  return (
    <Drawer open onClose={onClose} width={720} maximizable maximizedWidth="min(1180px, calc(100vw - 96px))" className="rounded-l-xl">
      <div className="flex shrink-0 flex-col gap-1 border-b border-border px-5 py-3.5 pr-24">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold text-text">{name}</div>
          <div className="text-caption text-text-muted">
            {rows.length}건의 {isPartner ? '시공' : '거래'} 이력
          </div>
        </div>
        <div className="text-caption text-text-muted">{headerHint}</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute right-4 top-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-title-sm leading-none text-text-muted hover:bg-surface-subtle hover:text-text"
        >
          ×
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5">
        {isLoading && <Skeleton className="my-4 h-24 w-full" />}
        {!isLoading && rows.length === 0 && (
          <div className="py-16 text-center text-body-sm text-text-muted">이력이 없습니다.</div>
        )}

        {rows.map((row) => {
          const isEditing = editingKey === row.rowKey;
          // 칩 on 상태가 반영할 현재 메모 값 = textarea에 보이는 값 (편집 중이면 draft, 아니면 빈칸)
          const chipValue = isEditing ? draft : '';
          return (
            <WorkItemCard
              key={row.rowKey}
              siteName={row.siteName}
              workTypeName={workTypeName(row.workType)}
              customerName={row.customerName}
              phase={row.phase}
              locked={row.locked}
              timeline={row.timeline}
              rating={row.rating}
              evalMemo={row.evalMemo}
              hasWorkType={!!row.workType}
              phrases={phrases}
              chipValue={chipValue}
              isEditing={isEditing}
              errorMessage={errorState?.key === row.rowKey ? errorState.message : null}
              draft={draft}
              onRate={(star) => {
                if (!row.workType || row.locked) return;
                save({ rowKey: row.rowKey, siteId: row.siteId, workTypeCode: row.workType, rating: star, evalMemo: row.evalMemo });
              }}
              onStartEdit={() => {
                if (row.locked) return;
                setEditingKey(row.rowKey);
                setDraft(row.evalMemo ?? '');
              }}
              onDraftChange={setDraft}
              onPhrase={(phrase) => {
                if (row.locked) return;
                const base = isEditing ? draft : row.evalMemo ?? '';
                if (!isEditing) setEditingKey(row.rowKey);
                setDraft(togglePhrase(base, phrase));
              }}
              onSaveMemo={() => {
                if (!row.workType || row.locked) return;
                save({ rowKey: row.rowKey, siteId: row.siteId, workTypeCode: row.workType, rating: row.rating, evalMemo: draft.trim() });
              }}
            />
          );
        })}
      </div>
    </Drawer>
  );
}

/** 자주 쓰는 문구 칩 — FilterChip은 단일 accent라 긍정/부정 2색을 못 담아 토큰으로 직접 작성(ui-core 토큰 사용) */
function PhraseChip({ phrase, on, onClick }: { phrase: Phrase; on: boolean; onClick: () => void }) {
  const tone =
    phrase.tone === 'pos'
      ? on
        ? 'bg-group text-group-onPrimary'
        : 'bg-group-subtle text-group'
      : on
        ? 'bg-danger text-white'
        : 'bg-[var(--status-danger-50)] text-danger';
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${tone}`}
    >
      {phrase.label}
    </button>
  );
}

/** 이력 타임라인 표 — 라벨 | 값 2열 그리드. muted 칸은 placeholder(대기 색) */
function Timeline({ rows }: { rows: TimelineRow[] }) {
  return (
    <div className="mt-2.5 overflow-hidden rounded-lg border border-border">
      {rows.map((r, i) => (
        <div
          key={r.label}
          className={`grid grid-cols-[62px_1fr] items-center gap-2 px-2.5 py-1.5 text-caption ${i > 0 ? 'border-t border-border' : ''}`}
        >
          <span className="font-semibold text-text-muted">{r.label}</span>
          <span className={r.muted ? 'text-text-disabled' : r.tone === 'ok' ? 'text-success' : 'text-text-secondary'}>
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function WorkItemCard({
  siteName, workTypeName, customerName, phase, locked, timeline, rating, evalMemo,
  hasWorkType, phrases, chipValue, isEditing, errorMessage, draft,
  onRate, onStartEdit, onDraftChange, onPhrase, onSaveMemo,
}: {
  siteName: string | null;
  workTypeName: string | null;
  customerName: string | null;
  phase: Phase;
  locked: boolean;
  timeline: TimelineRow[];
  rating: number | null;
  evalMemo: string | null;
  hasWorkType: boolean;
  phrases: Phrase[];
  chipValue: string;
  isEditing: boolean;
  errorMessage: string | null;
  draft: string;
  onRate: (star: number) => void;
  onStartEdit: () => void;
  onDraftChange: (value: string) => void;
  onPhrase: (phrase: string) => void;
  onSaveMemo: () => void;
}) {
  // 평가는 언제든 가능 — 현장 완료(locked)이면 읽기만
  const ratingEnabled = !locked && hasWorkType;
  const meta = PHASE_META[phase];
  const showTextarea = isEditing || !evalMemo;

  return (
    <div className="flex gap-5 border-b border-border py-4.5 last:border-b-0">
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="truncate text-[14px] font-extrabold text-text">{siteName ?? '-'}</span>
          {workTypeName && (
            <span className="shrink-0 rounded-md bg-point-subtle px-2 py-0.5 text-[10.5px] font-bold text-point">
              {workTypeName}
            </span>
          )}
          <Badge status={meta.badge} className="shrink-0">
            {meta.label}
          </Badge>
        </div>
        {customerName && <div className="mb-1 text-caption text-text-muted">{customerName} 고객님</div>}
        <Timeline rows={timeline} />
      </div>

      <div className="w-[288px] shrink-0 rounded-xl bg-surface-subtle p-3.5">
        <Rating value={rating ?? 0} size="sm" readOnly={!ratingEnabled} className="mb-2" onChange={ratingEnabled ? onRate : undefined} />
        {errorMessage && (
          <Alert kind="error" className="mb-2">{errorMessage}</Alert>
        )}

        {locked ? (
          <>
            <div className="mb-2 text-caption text-text-muted">완료된 현장의 평가는 수정할 수 없어요</div>
            {evalMemo ? (
              <div className="whitespace-pre-wrap text-caption leading-relaxed text-text-secondary">{evalMemo}</div>
            ) : (
              <div className="rounded-md border border-dashed border-border px-2.5 py-3 text-center text-caption text-text-muted">
                남긴 평가가 없어요
              </div>
            )}
          </>
        ) : showTextarea ? (
          <>
            <div className="mb-1.5 text-[11px] text-text-muted">자주 쓰는 문구를 눌러 메모에 넣어요</div>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {phrases.map((p) => (
                <PhraseChip key={p.label} phrase={p} on={chipValue.includes(p.label)} onClick={() => onPhrase(p.label)} />
              ))}
            </div>
            <textarea
              value={isEditing ? draft : ''}
              onChange={(e) => onDraftChange(e.target.value)}
              onFocus={() => { if (!isEditing) onStartEdit(); }}
              placeholder={hasWorkType ? '이 작업에 대한 평가 메모를 남겨보세요' : '공종 정보가 없어 평가할 수 없습니다'}
              maxLength={500}
              rows={3}
              disabled={!hasWorkType}
              className="w-full rounded-md border border-border bg-surface-raised p-2 text-caption text-text outline-none focus:border-group disabled:opacity-60"
            />
            <Button variant="primary" size="sm" onClick={onSaveMemo} disabled={!hasWorkType} className="mt-1.5">
              저장
            </Button>
          </>
        ) : (
          <>
            <div className="whitespace-pre-wrap text-caption leading-relaxed text-text-secondary">{evalMemo}</div>
            <Button variant="ghost" size="sm" onClick={onStartEdit} className="mt-2 px-0">
              메모 수정
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
